import { EventObject } from '@xstate/store-react';
import { InitOptions } from '@domgen/checkout-sdk-types';
import { AppRoute } from '../../../enums/appRoutes';
import { FormKey } from '../../../enums/formKey';
import { StepKey } from '../../../enums/stepKey';
import { StoreContext } from '../../../models/store/context';
import { StoreEvent } from '../../../enums/storeEvent';
import { getAuthToken } from '../../../services/api/getAuthToken';
import { store } from '../../store';
import { log } from '@domgen/shared/utils/log';

/**
 * Initiates checkout preparation from quote data.
 *
 * Fetches an auth token, builds checkout payload, dispatches SET_CHECKOUT on success,
 * and dispatches CHECKOUT_FAILED on validation or request errors.
 *
 * @param context Current store context.
 * @param event Event containing UI source information for analytics state.
 * @param enqueue Store enqueue helpers used for side effects and navigation emission.
 * @returns Updated context with checkout loading state and sales widget flag.
 */
export const continueToCheckout = <T extends EventObject = EventObject>(
  context: StoreContext,
  event: T & { data: { continueToCheckoutFromModal: boolean } },
  enqueue: {
    effect: (effect: () => Promise<void>) => void;
    emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void };
  },
): StoreContext => {
  log.debug({ message: StoreEvent.CONTINUE_TO_CHECKOUT });
  enqueue.effect(async () => {
    try {
      const quote = context.quote?.data;
      if (!quote) {
        store.send({ type: StoreEvent.CHECKOUT_FAILED });
        return;
      }

      const personId = quote.personId;
      const offerReference = quote.offer.offerReference;
      const offer = quote.offer;
      const customer = context.form[StepKey.PERSONAL_DETAILS];
      const brandCode = import.meta.env.VITE_BRAND_CODE as 'WHIRL' | 'KHAID' | 'MYTAG' | 'AMANA';

      if (!personId || !offerReference || !offer || !customer) {
        store.send({ type: StoreEvent.CHECKOUT_FAILED });
        return;
      }

      const { token } = await getAuthToken({
        personId,
        offerReferences: [offerReference],
        brand: brandCode,
      });

      const subs = { ...offer.contracts[0].contractAttributes.subscription };
      const offers = [
        {
          ...offer,
          contracts: [
            {
              ...offer.contracts[0],
              contractAttributes: {
                ...offer.contracts[0].contractAttributes,
                ...subs,
              },
            },
          ],
        },
      ];

      const checkoutData: InitOptions = {
        proposition: { type: 'monthly_subs' },
        authToken: token,
        basket: {
          channelSourceData: {
            checkoutSourceRef: `wpus-usa-${import.meta.env.VITE_BRAND_CODE.trim().toLowerCase()}-olr`,
            interactionId: context.app.session?.id ?? quote.personId,
          },
          offers,
        },
        customer: {
          title: '',
          firstName: customer[FormKey.FIRST_NAME],
          lastName: customer[FormKey.LAST_NAME],
          emailAddress: customer[FormKey.EMAIL],
          personId,
          address: {
            countryCode: customer[FormKey.COUNTRY_CODE] ?? 'USA',
            number: customer[FormKey.ADDRESS_LINE_1],
            street: customer[FormKey.ADDRESS_LINE_2],
            postalCode: customer[FormKey.POSTAL_CODE],
            stateCode: customer[FormKey.STATE_CODE],
            city: customer[FormKey.CITY],
          },
        },
      };

      store.send({ type: StoreEvent.SET_CHECKOUT, data: checkoutData });
    } catch (error) {
      console.error('Error during checkout process', error);
      store.send({ type: StoreEvent.CHECKOUT_FAILED });
    }
  });

  enqueue.emit.navigate({ path: AppRoute.CHECKOUT });

  return {
    ...context,
    checkout: { loading: true },
    confirmation: {
      ...context.confirmation,
      offerData: {
        ...context.confirmation?.offerData,
      },
    },
    gaEventData: {
      ...context?.gaEventData,
      salesWidgetClicked: event.data.continueToCheckoutFromModal,
    },
  };
};
